/*
 * ===============================================================================================================
 *                                Copyright 2024, Blue Yonder Group, Inc.
 *                                           All Rights Reserved
 *
 *                               THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
 *                                          BLUE YONDER GROUP, INC.
 *
 *
 *                         The copyright notice above does not evidence any actual
 *                                 or intended publication of such source code.
 *
 * ===============================================================================================================
 */
import { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Autocomplete, Box, Button, TextField } from "@mui/material";

import useStyle from "./styles";
import { MESSAGES, TITLES, REALM_NAME_REGEX } from "./constants";
import Loader from "../../shared/loader/Loader";
import FormDialog from "../../shared/dialog/FormDialog";
import { selectError } from "../../store/error/errorSelector";
import InfoMessage from "../../shared/infoMessage/InfoMessage";
import { selectLoader } from "../../store/loader/loaderSelector";
import { createRealm, editRealm } from "../../store/realm/realm";
import { selectSelectedRealm } from "../../store/realm/realmSelector";
import { showErrorAction } from "../../store/error/errorActionCreator";
import { CustomTooltip } from "../../shared/customTooltip/CustomTooltip";
import { hideLoaderAction } from "../../store/loader/loaderActionCreator";
import { selectEnvTypes } from "../../store/stystemSpec/systemSpecSelector";
import { selectSelectedCustomer } from "../../store/customer/customerSelector";
import { setSnackBarDisplay } from "../../store/snackBar/snackBarActionCreator";
import { selectSelectedSubEnterprise } from "../../store/subEnterprise/subEnterpriseSelector";
import { IAddRealmDialogProps, IFormValuesRealm, IRealmRequest } from "../../models/Realms/RealmContracts";

const AddRealmDialog = (props: IAddRealmDialogProps) => {
  const formDefaultValues: IRealmRequest = {
    id: '',
    displayName: '',
    name: '',
    email: '',
  };

  const formErrorDefaultValues = {
    emailError: false,
    nameError: false,
    errorHelperTextEmail: "",
    errorHelperTextName: "",
  };

  const snackBarAlertDefaultProps = {
    show: true,
    autoHideDuration: 5000,
    message: "Customer added!",
    severity: "success" as "success" | "error",
  };
  const classes = useStyle();
  const dispatch = useDispatch();

  const loading = useSelector(selectLoader);
  const errorMesaage = useSelector(selectError);
  const selectedRealm = useSelector(selectSelectedRealm);
  const selectedEnvTypes = useSelector(selectEnvTypes);
  const selectedCustomer = useSelector(selectSelectedCustomer);
  const selectedSubEnterprise = useSelector(selectSelectedSubEnterprise);

  const [showLoader, showHideLoader] = useState(false);
  const [formErrorState, updateFormError] = useState(formErrorDefaultValues);
  const [formValues, updateFormValues] = useState<IRealmRequest>(formDefaultValues);

  useEffect(() => {
    updateFormValues({ ...formDefaultValues });
    updateFormError({ ...formErrorDefaultValues });
    dispatch(hideLoaderAction());
    dispatch(showErrorAction({ message: '' }))
  }, [props.openModal]);

  useEffect(() => {
    if (props.isEdit) {
      updateFormValues({
        name: props.realmData.name,
        id: props.realmData.id,
        displayName: props.realmData.displayName,
        email: props.realmData.contactEmail,
        envType: props.realmData.customerEnvType
      });
    }
  }, [props.isEdit, props.realmData]);

  const handleClose = () => {
    props.onClose();
  };

  const updateFormFields = (event: { target: { name: string; value: string } }) => {
    const { name, value } = event.target;
    updateFormValues({ ...formValues, [name]: value });
    updateFormError({ ...formErrorState, ...formErrorDefaultValues });
  };

  const onChangeValue = (key: string, value: any) => {
    updateFormValues({ ...formValues, [key]: value });
    updateFormError({ ...formErrorState, ...formErrorDefaultValues });
  };

  const validateRequired = (values: IFormValuesRealm) => {
    const errorObj: any = {};
    if (!values.name || !REALM_NAME_REGEX.test(values.name || "")) {
      errorObj.nameError = true;
      errorObj.errorHelperTextName = !values.name ?MESSAGES.REALM_NAME_REQUIRED: MESSAGES.REALM_NAME_REGEX_ERROR;
      updateFormError({ ...formErrorState, ...errorObj });
    } else if (!values.email) {
      errorObj.emailError = true;
      errorObj.errorHelperTextEmail = MESSAGES.EMAIL_REQUIRED;
      updateFormError({ ...formErrorState, ...errorObj });
    }  else return true;
  };

  const handleSubmit = async () => {
    let isSuccess = false;
    if (validateRequired(formValues)) {
      showHideLoader(true);
      if (props.isEdit) {
        const payload: any = {
          id: selectedRealm.id,
          customerId: selectedCustomer.id,
          displayName: formValues.displayName,
          subEnterpriseId: selectedSubEnterprise.id,
        };
        !props.realmData.name && (payload.name = formValues.name);
        isSuccess = await editRealm(dispatch, payload);
      } else {
        const payload: any = {
          displayName: formValues.displayName || '',
          name: formValues.name,
          contactEmail: formValues.email,
          geo: formValues.geo,
          instance: formValues.instance,
          customerEnvType: formValues.envType,
          customerId: selectedCustomer.id,
          subEnterpriseId: selectedSubEnterprise.id,
        };
        isSuccess = await createRealm(dispatch, payload);
      }
      showHideLoader(false);
      if (isSuccess) {
        handleClose();
        dispatch(
          setSnackBarDisplay({
            ...snackBarAlertDefaultProps,
            show: true,
            message: props.isEdit ? MESSAGES.EDIT_REALM_SUCCESS : isSuccess + ' ' + MESSAGES.ADD_REALM_SUCCESS,
          })
        );
      }
    } else {
    }
  };

  return (
    <Fragment>
      <FormDialog
        dialogAction1={
          <Button
            color="primary"
            disableElevation
            name="cancelBtn"
            variant="outlined"
            onClick={handleClose}
          >
            {TITLES.CANCEL}
          </Button>
        }
        dialogAction2={
          <Button
            color="primary"
            disableElevation
            variant="contained"
            disabled={showLoader}
            onClick={handleSubmit}
            name="createCustomerBtn"
          >
            {TITLES.ADD}
          </Button>
        }
        handleClose={handleClose}
        openModal={props.openModal}
        dialogTitle={props.isEdit ? TITLES.EDIT_REALM : TITLES.ADD_REALM}
      >
        <Box className={classes.modalFormContent}>
          <TextField
            fullWidth
            id="realmname"
            name="name"
            variant="standard"
            required
            label={TITLES.REALM_NAME}
            onChange={updateFormFields}
            value={formValues.name}
            inputProps={{ maxLength: 64 }}
            className={classes.modalTextBox}
            error={formErrorState.nameError}
            helperText={formErrorState.errorHelperTextName}
            disabled={(props.isEdit && props.realmData.name) ? true : showLoader}
          />
          <CustomTooltip title={MESSAGES.REALM_NAME_INFO}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            fullWidth
            id="Displayname"
            name="displayName"
            variant="standard"
            disabled={showLoader}
            label={TITLES.DISPLAY_NAME}
            onChange={updateFormFields}
            value={formValues.displayName}
            inputProps={{ maxLength: 32 }}
            className={classes.modalTextBox}
          />
          <CustomTooltip title={MESSAGES.REALM_DISPLAY_NAME_INFO}></CustomTooltip>
        </Box>
        <Box className={classes.modalFormContent}>
          <TextField
            required
            fullWidth
            id="email"
            name="email"
            variant="standard"
            label={TITLES.EMAIL}
            value={formValues.email}
            onChange={updateFormFields}
            error={formErrorState.emailError}
            className={classes.modalTextBox}
            disabled={props.isEdit ? true : showLoader}
            helperText={formErrorState.errorHelperTextEmail}
          />
          <CustomTooltip title={MESSAGES.REALM_EMAIL_INFO}></CustomTooltip>
        </Box>
        <Box className={classes.dropDownWrap}>
          <Autocomplete
            id="single-select"
            value={formValues.envType}
            className={classes.dropDown}
            aria-controls="options-list"
            getOptionLabel={(option: string) => option}
            disabled={props.isEdit ? true : showLoader}
            options={selectedEnvTypes || []}
            onChange={(_event, value) => onChangeValue('envType', value)}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                value={formValues.envType}
                label={TITLES.CUSTOMER_ENVIRONMENT_TYPE}
                placeholder={TITLES.CUSTOMER_ENVIRONMENT_TYPE}
              />
            )}
            ListboxProps={{ id: "options-list" }}
          />
          <CustomTooltip title={MESSAGES.REALM_CUSTOMER_ENVIRONMENT_TYPE_INFO}></CustomTooltip>
        </Box>
        <InfoMessage
          message={MESSAGES.REALM_INFO}
          subText={`${selectedSubEnterprise.accountKey}-${formValues.envType?.toLowerCase() || '{Customer Environment Type}'}{Instance count}`}
        />
        {/* {errorMesaage && <ErrorMessage message={errorMesaage.message} />} */}
        <Loader showLoader={showLoader} isCenter />
      </FormDialog>
    </Fragment>
  );
};

export default AddRealmDialog;
